import { useEffect, useState } from 'react';
import { Queue } from '../types';
import apiService from '../services/api';

const queueCache = new Map<number, Queue>();
const inflight = new Map<number, Promise<Queue>>();

export function setCachedQueue(queue: Queue) {
  queueCache.set(queue.queue_id, queue);
}

export function clearCachedQueue(queueId: number) {
  queueCache.delete(queueId);
}

export function useQueue(queueId: number | undefined): { queue: Queue | null; isLoading: boolean } {
  const cached = queueId !== undefined ? queueCache.get(queueId) ?? null : null;
  const [queue, setQueue] = useState<Queue | null>(cached);
  const [isLoading, setIsLoading] = useState<boolean>(queueId !== undefined && cached === null);

  useEffect(() => {
    if (queueId === undefined) {
      setQueue(null);
      setIsLoading(false);
      return;
    }

    const cachedNow = queueCache.get(queueId) ?? null;
    setQueue(cachedNow);
    setIsLoading(cachedNow === null);

    let cancelled = false;
    const existing = inflight.get(queueId);
    const promise = existing ?? apiService.getQueue(queueId).then((data) => {
      queueCache.set(queueId, data);
      inflight.delete(queueId);
      return data;
    }).catch((err) => {
      inflight.delete(queueId);
      throw err;
    });
    if (!existing) inflight.set(queueId, promise);

    promise
      .then((data) => {
        if (!cancelled) {
          setQueue(data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch queue details:', err);
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [queueId]);

  return { queue, isLoading };
}
